import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './ShareClosetButton.module.css';

/**
 * A button that shares a closet (profile page) link. Uses the Web Share API
 * where available (opens the device's native share sheet - Instagram,
 * TikTok, Messages, etc. all show up there if installed), and falls back to
 * copying the link to the clipboard otherwise (e.g. most desktop browsers).
 * If the clipboard write is denied (browsers can refuse this for several
 * reasons), it falls back further to a manually-selectable text field so
 * the link is never unreachable.
 *
 * @component
 * @param {Object} props
 * @param {string} props.url - Absolute URL of the closet to share
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 */
const ShareClosetButton = props => {
  const { url, className, rootClassName } = props;
  const intl = useIntl();
  const [justCopied, setJustCopied] = useState(false);
  const [showManualCopy, setShowManualCopy] = useState(false);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (showManualCopy && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showManualCopy]);

  const handleClick = async () => {
    const shareData = {
      title: intl.formatMessage({ id: 'ShareClosetButton.shareTitle' }),
      text: intl.formatMessage({ id: 'ShareClosetButton.shareText' }),
      url,
    };

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // Ignore - thrown when the user just closes the share sheet.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setShowManualCopy(false);
      setJustCopied(true);
      timeoutRef.current = setTimeout(() => setJustCopied(false), 2000);
    } catch (e) {
      // Clipboard access can be denied by the browser - fall back to a
      // field the person can select and copy themselves.
      setShowManualCopy(true);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={css.wrapper}>
      <button type="button" className={classes} onClick={handleClick}>
        {justCopied ? (
          <FormattedMessage id="ShareClosetButton.copied" />
        ) : (
          <FormattedMessage id="ShareClosetButton.label" />
        )}
      </button>
      {showManualCopy ? (
        <input
          ref={inputRef}
          className={css.manualCopyInput}
          type="text"
          readOnly
          value={url}
          onFocus={e => e.target.select()}
          aria-label={intl.formatMessage({ id: 'ShareClosetButton.manualCopyLabel' })}
        />
      ) : null}
    </div>
  );
};

export default ShareClosetButton;
